import { ActivatedRouteSnapshot, PRIMARY_OUTLET, Router, RouterStateSnapshot, UrlSegmentGroup, UrlTree } from '@angular/router';
import { ClientService } from '../services/client.service';
import { MenuItem } from '../modules/base/base.model';
import { ACCESS_LEVELS } from '../constants';
import { EADMIN_AccessControl, ESMS_AccessControl } from '../guards/feature-access.model';

export type RouteAccessConfig =
  | string
  | { accessKey: string; isAdminRoute?: boolean; extraPredicate?: () => boolean };

function stripQueryAndFragment(url: string): string {
  if (!url) {
    return '';
  }
  const queryIndex = url.indexOf('?');
  const hashIndex = url.indexOf('#');
  let end = url.length;
  if (queryIndex >= 0) {
    end = Math.min(end, queryIndex);
  }
  if (hashIndex >= 0) {
    end = Math.min(end, hashIndex);
  }
  return url.slice(0, end);
}

function collectPrimaryPathSegments(group: UrlSegmentGroup | undefined, parts: string[]): void {
  if (!group) {
    return;
  }
  for (const seg of group.segments) {
    parts.push(seg.path);
  }
  collectPrimaryPathSegments(group.children[PRIMARY_OUTLET], parts);
}

function getPrimaryPathFromUrlTree(urlTree: UrlTree): string {
  const parts: string[] = [];
  collectPrimaryPathSegments(urlTree.root.children[PRIMARY_OUTLET], parts);
  return parts.join('/');
}

function normalizeMenuRoutePath(route: string): string {
  return stripQueryAndFragment(route);
}

function resolveRouteAccess(
  raw: RouteAccessConfig | undefined,
  fallback: string
): { accessKey: string; isAdminRoute: boolean; extraPredicate?: () => boolean } {
  if (raw === null) return { accessKey: fallback, isAdminRoute: false };
  if (typeof raw === 'string') return { accessKey: raw, isAdminRoute: false };
  return {
    accessKey: raw.accessKey,
    isAdminRoute: raw.isAdminRoute === true,
    extraPredicate: raw.extraPredicate
  };
}

/**
 * Checks route authorization based on menu items and access controls
 * @param route The activated route snapshot
 * @param state The router state snapshot
 * @param router The Angular router
 * @param clientService The client service for user and menu data
 * @param routeCheckAccessMap Map of route paths to access keys (or { accessKey, isAdminRoute?, extraPredicate? } for admin-backed SMS routes)
 * @param defaultFeatureType Default feature type if not specified
 * @param featureAccessMap Optional map for feature-level access controls
 * @returns true if access is allowed, false otherwise
 */
export function checkRouteAuthorization(
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
  router: Router,
  clientService: ClientService,
  routeCheckAccessMap: Record<string, RouteAccessConfig>,
  defaultFeatureType: string,
  featureAccessMap?: { [key: string]: { smsAccessControl?: ESMS_AccessControl, adminAccessControl?: EADMIN_AccessControl } },
): boolean {
  let activeRoute = route;
  while (activeRoute.firstChild) {
    activeRoute = activeRoute.firstChild;
  }

  const pathOnly = stripQueryAndFragment(state.url);
  const urlTree = router.parseUrl(pathOnly.startsWith('/') ? pathOnly : `/${pathOnly}`);
  const currentUrl = getPrimaryPathFromUrlTree(urlTree);

  const menuItems = clientService.menuItems();

  if (currentUrl === '') {
    return true;
  }

  if (menuItems.length === 0) {
    const targetUrl = clientService.getRoute(true);
    router.navigateByUrl(targetUrl);
    return false;
  }

  // Check feature-level access first (from access.guard logic)
  if (featureAccessMap && activeRoute.routeConfig?.path) {
    const featureInfo = featureAccessMap[activeRoute.routeConfig.path];
    if (featureInfo) {
      const { smsAccessControl, adminAccessControl } = featureInfo;
      let showFeatureSMS = true;
      let showFeatureAdmin = true;

      if (smsAccessControl) {
        const clientFeatureSMS = clientService.client().features?.sms?.[smsAccessControl] ?? ACCESS_LEVELS.NO_ACCESS;
        const userRoleSMS = clientService.user().role.features?.sms?.[smsAccessControl] ?? ACCESS_LEVELS.NO_ACCESS;
        showFeatureSMS = clientFeatureSMS !== ACCESS_LEVELS.NO_ACCESS && userRoleSMS !== ACCESS_LEVELS.NO_ACCESS;
      }

      if (adminAccessControl) {
        const clientFeatureAdmin = clientService.client().features?.admin?.[adminAccessControl] ?? ACCESS_LEVELS.NO_ACCESS;
        const userRoleAdmin = clientService.user().role.features?.admin?.[adminAccessControl] ?? ACCESS_LEVELS.NO_ACCESS;
        showFeatureAdmin = clientFeatureAdmin !== ACCESS_LEVELS.NO_ACCESS && userRoleAdmin !== ACCESS_LEVELS.NO_ACCESS;
      }

      if (!showFeatureSMS || !showFeatureAdmin) {
        router.navigate(['/login']);
        return false;
      }
    }
  }

  const urlSegment = currentUrl?.split('/')[1] || '';
  const raw =
    routeCheckAccessMap[activeRoute.routeConfig?.path || ''] ??
    routeCheckAccessMap[urlSegment] ??
    urlSegment ??
    '';
  const { accessKey: checkAccessKey, isAdminRoute, extraPredicate } = resolveRouteAccess(raw, urlSegment || '');
  if (!checkAccessKey) {
    return true; // Allow access if no specific access key is required
  }
  const featureType = isAdminRoute ? 'admin' : (currentUrl.includes('admin') ? 'admin' : defaultFeatureType);
  
  // Access Checks.
  // `extraPredicate` acts as an alternate allow condition for routes that are intentionally not present in `menuItems`
  // (e.g. deep-links like badges/karma-points).
  const hasMenuOrRoleAccess =
    isRouteInMenu(currentUrl, menuItems) || clientService.checkAccess(checkAccessKey, ACCESS_LEVELS.VIEW_ONLY, featureType);
  const hasExtraPredicateAccess = Boolean(extraPredicate && extraPredicate());
  const isUnauthorisedRoute = !hasMenuOrRoleAccess && !hasExtraPredicateAccess;

  if (isUnauthorisedRoute === true) {
    if (currentUrl === 'admin/companyInfo' && clientService.isGlobalAdmin()) {
      return true;
    }

    if (currentUrl === 'admin/users' && clientService.isGlobalAdmin()) {
      if (clientService.checkAccess(checkAccessKey, ACCESS_LEVELS.VIEW_ONLY, featureType)) {
        return true;
      }

      router.navigate(['/admin/companyInfo']);
      return false;
    }

    router.navigate(['/accessDenied']);
    return false;
  }

  return true;
}

function isRouteInMenu(route: string, menuItems: MenuItem[]): boolean {
  const normalizedRoute = normalizeMenuRoutePath(route);
  for (const item of menuItems) {
    if (normalizeMenuRoutePath(item.route) === normalizedRoute) {
      return true;
    }
    if (item.children && isRouteInMenu(route, item.children)) {
      return true;
    }
  }
  return false;
}